<template>
  <section class="academy-control">
    <header class="control-header"><div><h2>Audit Callings</h2><p>Telefonberatung pro Center dokumentieren und Feedback nachhalten.</p></div><div class="control-tools"><v-btn outlined color="#58d388" :loading="loading" @click="load">Aktualisieren</v-btn><v-btn color="#58d388" class="black--text" :disabled="loading || !centers.length" @click="open()">Audit erfassen</v-btn></div></header>
    <ControlSettings area="audits" />
    <v-alert v-if="error" type="error" text>{{ error }}</v-alert>
    <v-progress-linear v-if="loading" indeterminate color="#58d388" class="mb-3" />
    <div class="control-grid mb-5"><button v-for="center in overviewCenters" :key="center._id" type="button" class="control-card text-left" :class="{ selected: studioId === center._id }" @click="selectCenter(center._id)"><h3>{{ center.name }}</h3><p class="control-note">{{ center.date ? 'Letztes Audit: ' + date(center.date) + ' · Note ' + center.grade : 'Noch kein Audit erfasst' }}</p><div class="control-meta mt-2">{{ center.count || 0 }} Audits · {{ center.open || 0 }} mit offenem Feedback</div></button></div>
    <div class="control-tools mb-3"><v-select v-model="studioId" :items="overviewCenters" item-text="name" item-value="_id" label="Center filtern" clearable outlined dark hide-details @change="changeFilter" /><span class="control-note">{{ total }} Gesprächseinträge</span></div>
    <div class="control-history"><article v-for="row in rows" :key="row._id" class="control-card"><h3>{{ row.studioName }} · {{ date(row.date) }}, {{ row.time }} Uhr</h3><p><strong>{{ row.employeeName }}</strong> · Note {{ row.grade }} · Gesprächsführung: {{ conversationLabel(row.conversation) }}</p><p v-if="row.positive"><strong>Positiv aufgefallen</strong><br>{{ row.positive }}</p><p v-if="row.potential"><strong>Potentialfelder</strong><br>{{ row.potential }}</p><p class="control-note">Feedback Mitarbeiter: {{ feedbackLabel(row.employeeFeedback) }}<br>Feedback Centerleitung: {{ feedbackLabel(row.centerFeedback) }}</p><div class="control-actions"><v-btn outlined color="#58d388" @click="open(row)">Bearbeiten</v-btn><v-btn text color="#ffb5af" :disabled="busy" @click="remove(row)">Entfernen</v-btn></div></article></div>
    <div v-if="!rows.length && !loading" class="control-empty">Noch keine Audit Callings für diese Auswahl.</div>
    <v-pagination v-if="total > 25" v-model="page" :length="Math.ceil(total / 25)" :total-visible="5" color="#009640" dark @input="load" />
    <v-dialog v-model="dialog" dark scrollable max-width="60rem" :persistent="busy" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="form" class="control-dialog audit-dialog">
        <v-card-title class="audit-header">
          <span class="audit-header-icon" aria-hidden="true"><v-icon>mdi-phone-outline</v-icon></span>
          <div class="audit-heading">
            <span class="audit-eyebrow">Leadership · Gesprächsprotokoll</span>
            <h2>{{ form._id ? 'Audit bearbeiten' : 'Audit Calling erfassen' }}</h2>
          </div>
          <v-btn icon class="audit-close" :disabled="busy" aria-label="Schliessen" @click="close"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="audit-body">
          <section class="audit-section" aria-labelledby="audit-details-title">
            <div class="audit-section-heading">
              <span class="audit-step" aria-hidden="true">01</span>
              <h3 id="audit-details-title">Gesprächsdaten</h3>
            </div>
            <div class="audit-fields">
              <v-select v-model="form.studio" :items="formCenters" item-text="name" item-value="_id" label="Center" dark outlined dense hide-details="auto" color="#58d388" :disabled="busy" @change="form.employee = null" />
              <v-select v-model="form.employee" :items="employees" item-text="name" item-value="user" label="Mitarbeiter" dark outlined dense hide-details="auto" color="#58d388" :disabled="busy" />
              <v-text-field v-model="form.date" type="date" label="Audit-Datum" dark outlined dense hide-details="auto" color="#58d388" :disabled="busy" />
              <v-text-field v-model="form.time" type="time" label="Uhrzeit" dark outlined dense hide-details="auto" color="#58d388" :disabled="busy" />
            </div>
          </section>

          <section class="audit-section" aria-labelledby="audit-assessment-title">
            <div class="audit-section-heading">
              <span class="audit-step" aria-hidden="true">02</span>
              <h3 id="audit-assessment-title">Beobachtung & Bewertung</h3>
            </div>
            <div class="audit-fields">
              <v-textarea v-model="form.positive" label="Positiv aufgefallen" placeholder="Was ist im Gespräch gut gelungen?" dark outlined auto-grow rows="3" counter="4000" color="#58d388" :disabled="busy" />
              <v-textarea v-model="form.potential" label="Potentialfelder" placeholder="Wo gibt es Möglichkeiten zur Verbesserung?" dark outlined auto-grow rows="3" counter="4000" color="#58d388" :disabled="busy" />
              <v-select v-model="form.conversation" :items="conversationOptions" label="Gesprächsführung" hint="Durchgängige Gesprächsführung erfüllt?" persistent-hint dark outlined dense color="#58d388" :disabled="busy" />
              <v-select v-model="form.grade" :items="grades" label="Schulnote (1–6)" hint="6 ist die beste Note." persistent-hint dark outlined dense color="#58d388" :disabled="busy" />
            </div>
          </section>

          <section class="audit-section" aria-labelledby="audit-feedback-title">
            <div class="audit-section-heading">
              <span class="audit-step" aria-hidden="true">03</span>
              <h3 id="audit-feedback-title">Feedback dokumentieren</h3>
              <span class="audit-optional">Optional</span>
            </div>
            <p class="audit-section-note">Wer hat wann Rückmeldung gegeben? Die Einträge bleiben intern und senden keine Bewertung an Mitarbeitende.</p>
            <div class="audit-feedback-grid">
              <div class="audit-feedback-card">
                <h4><v-icon aria-hidden="true">mdi-account-outline</v-icon>An Mitarbeiter</h4>
                <div class="audit-feedback-fields">
                  <v-text-field v-model="form.employeeFeedback.by" label="Erfolgte durch" dark outlined dense hide-details="auto" color="#58d388" :disabled="busy" />
                  <v-text-field v-model="form.employeeFeedback.date" type="date" label="Am" dark outlined dense hide-details="auto" color="#58d388" :disabled="busy" />
                </div>
              </div>
              <div class="audit-feedback-card">
                <h4><v-icon aria-hidden="true">mdi-account-tie-outline</v-icon>An Centerleitung</h4>
                <div class="audit-feedback-fields">
                  <v-text-field v-model="form.centerFeedback.by" label="Erfolgte durch" dark outlined dense hide-details="auto" color="#58d388" :disabled="busy" />
                  <v-text-field v-model="form.centerFeedback.date" type="date" label="Am" dark outlined dense hide-details="auto" color="#58d388" :disabled="busy" />
                </div>
              </div>
            </div>
          </section>
          <v-alert v-if="formError" type="error" text class="audit-error" role="alert">{{ formError }}</v-alert>
        </v-card-text>

        <v-card-actions class="audit-footer">
          <span class="audit-private"><v-icon aria-hidden="true">mdi-lock-outline</v-icon>Nur für die Leadership-Verwaltung</span>
          <div class="audit-footer-actions">
            <v-btn outlined class="audit-cancel" :disabled="busy" @click="close">Abbrechen</v-btn>
            <v-btn depressed color="#58d388" class="audit-save black--text" :loading="busy" :disabled="busy || uncertain || !form.studio || !form.employee" @click="save">Speichern</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>
<script>
import api from "@/api"
import ControlSettings from "./ControlSettings.vue"
export default {
  components: { ControlSettings },
  data: () => ({ rows: [], centers: [], people: [], overview: [], total: 0, page: 1, studioId: null, loading: false, error: '', loadId: 0,
    dialog: false, form: null, original: null, busy: false, uncertain: false, formError: '',
    conversationOptions: [{ text: 'Ja', value: 'yes' }, { text: 'Nein', value: 'no' }, { text: 'Teilweise', value: 'partly' }],
    grades: [6, 5.5, 5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1] }),
  computed: {
    overviewCenters() {
      const map = new Map(this.centers.map(center => [center._id, { ...center }]))
      this.overview.forEach(item => map.set(item._id, { ...(map.get(item._id) || {}), ...item }))
      return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'de-CH'))
    },
    formCenters() {
      const centers = [...this.centers]
      if (this.original && !centers.some(center => center._id === this.original.studio)) centers.push({ _id: this.original.studio, name: this.original.studioName })
      return centers
    },
    employees() {
      if (!this.form) return []
      const people = this.people.filter(person => person.centers.some(center => center.id === this.form.studio))
      if (this.original && this.original.studio === this.form.studio && !people.some(person => person.user === this.original.employee)) people.push({ user: this.original.employee, name: this.original.employeeName })
      return people
    },
  },
  mounted() { this.load() },
  methods: {
    date(value) { return value ? value.split('-').reverse().join('.') : '–' },
    conversationLabel(value) { return { yes: 'Ja', no: 'Nein', partly: 'Teilweise' }[value] || '–' },
    feedbackLabel(value) { return value && value.date ? `${value.by} am ${this.date(value.date)}` : 'Noch offen' },
    selectCenter(id) { this.studioId = this.studioId === id ? null : id; this.changeFilter() },
    changeFilter() { this.page = 1; this.load() },
    async load() {
      const request = ++this.loadId; this.loading = true; this.error = ''
      try {
        const data = await api.academyControl.audits(this.studioId, this.page)
        if (request !== this.loadId) return
        this.rows = data.rows; this.total = data.total; this.centers = data.centers; this.people = data.people; this.overview = data.overview
        if (!this.rows.length && this.page > 1) { this.page--; return this.load() }
      } catch (error) { if (request === this.loadId) this.error = 'Audit Callings konnten nicht geladen werden.' }
      finally { if (request === this.loadId) this.loading = false }
    },
    open(row = null) {
      if (this.busy) return
      this.original = row
      this.form = row ? JSON.parse(JSON.stringify(row)) : { studio: this.studioId, employee: null,
        date: new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Zurich' }).format(new Date()),
        time: new Intl.DateTimeFormat('de-CH', { timeZone: 'Europe/Zurich', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date()),
        positive: '', potential: '', conversation: null, grade: null, employeeFeedback: { by: '', date: '' }, centerFeedback: { by: '', date: '' } }
      this.formError = ''; this.uncertain = false; this.dialog = true
    },
    close() { if (this.busy) return; this.dialog = false; this.load() },
    async save() {
      if (this.busy || this.uncertain) return
      this.busy = true; this.formError = ''
      try {
        if (this.form._id) await api.academyControl.updateAudit(this.form._id, this.form)
        else await api.academyControl.createAudit(this.form)
        this.dialog = false; await this.load()
      } catch (error) {
        this.formError = (error.response && error.response.data.error) || 'Die Speicherbestätigung fehlt. Bitte schliessen und die aktualisierte Liste prüfen.'
        this.uncertain = !error.response || error.response.status === 409
      } finally { this.busy = false }
    },
    async remove(row) {
      if (this.busy || !confirm(`Audit vom ${this.date(row.date)} für ${row.studioName} ausblenden?`)) return
      this.busy = true
      try { await api.academyControl.deleteAudit(row._id, row.revision); await this.load() }
      catch (error) { this.error = (error.response && error.response.data.error) || 'Entfernen konnte nicht bestätigt werden. Bitte aktualisieren.' }
      finally { this.busy = false }
    },
  },
}
</script>
<style scoped src="./control.css"></style>

<style scoped>
.audit-dialog {
  border: 1px solid #343c37;
  border-radius: 1.25rem;
  overflow: hidden;
}

.audit-dialog .audit-header {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.875rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #303832;
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.12), transparent);
}

.audit-header-icon {
  display: grid;
  flex: 0 0 2.75rem;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 0.875rem;
  background: rgba(0, 150, 64, 0.13);
  color: #7ee5a6;
}

.audit-header-icon .v-icon { color: currentColor; font-size: 1.5rem; }
.audit-heading { flex: 1; min-width: 0; }
.audit-eyebrow { display: block; color: #92b49e; font-size: 0.75rem; line-height: 1.5; font-weight: 600; }
.audit-heading h2 { margin: 0.125rem 0 0; font-size: 1.375rem; font-weight: 600; line-height: 1.35; color: #fff; }
.audit-close { flex: 0 0 auto; }

.audit-dialog .audit-body {
  display: grid;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  overscroll-behavior: contain;
}

.audit-section {
  min-width: 0;
  padding: 1.125rem;
  border: 1px solid #303832;
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.015);
}

.audit-section-heading { display: flex; align-items: center; flex-wrap: wrap; gap: 0.625rem; margin-bottom: 1rem; }
.audit-step { display: grid; place-items: center; width: 1.75rem; height: 1.75rem; border-radius: 0.5rem; background: #263a2d; color: #94deb0; font-size: 0.75rem; font-weight: 600; }
.audit-dialog .audit-section-heading h3 { margin: 0; font-size: 1rem; font-weight: 600; line-height: 1.5; }
.audit-optional { margin-left: auto; color: #aeb8b1; font-size: 0.75rem; line-height: 1.5; }
.audit-section-note { margin: -0.25rem 0 1rem; color: #b8c0bb; font-size: 0.875rem; line-height: 1.5; }
.audit-fields, .audit-feedback-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
.audit-feedback-card { min-width: 0; padding: 1rem; border-radius: 0.75rem; background: rgba(255, 255, 255, 0.025); }
.audit-feedback-card h4 { display: flex; align-items: center; gap: 0.5rem; margin: 0 0 1rem; color: #e4e9e6; font-size: 1rem; font-weight: 500; line-height: 1.5; }
.audit-feedback-card h4 .v-icon { color: #9fb7a8; font-size: 1.25rem; }
.audit-feedback-fields { display: grid; gap: 1rem; }

.audit-dialog ::v-deep .v-input { min-width: 0; margin: 0; font-size: 1rem; }
.audit-dialog ::v-deep .v-label { font-size: 1rem; }
.audit-dialog ::v-deep .v-input__slot { background: rgba(0, 0, 0, 0.1); border-radius: 0.625rem; }
.audit-dialog ::v-deep .v-text-field__details { min-height: 1.125rem; margin-top: 0.375rem; }
.audit-dialog ::v-deep .v-messages,
.audit-dialog ::v-deep .v-counter { font-size: 0.75rem; line-height: 1.5; }
.audit-dialog ::v-deep input,
.audit-dialog ::v-deep textarea { font-size: 1rem; }
.audit-dialog ::v-deep input[type="date"],
.audit-dialog ::v-deep input[type="time"] { color-scheme: dark; min-width: 0; }
.audit-dialog ::v-deep textarea::placeholder { color: #a5afa9; opacity: 1; font-size: 0.875rem; }
.audit-dialog .audit-error { margin: 0; font-size: 1rem; }

.audit-dialog .audit-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #343c37;
  background: #1e2420;
}

.audit-private { display: inline-flex; align-items: center; gap: 0.375rem; color: #b8c0bb; font-size: 0.75rem; line-height: 1.5; }
.audit-private .v-icon { color: #94b49f; font-size: 1rem; }
.audit-footer-actions { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-left: auto; }
.audit-footer-actions .v-btn { min-height: 2.75rem; margin: 0; padding: 0.625rem 1.125rem; border-radius: 0.625rem; font-size: 0.875rem; font-weight: 600; text-transform: none; letter-spacing: 0; }
.audit-footer-actions .audit-cancel { border-color: #55605a; color: #e4e9e6; }

@media (max-width: 40rem) {
  .audit-dialog .audit-header { gap: 0.75rem; padding: 1rem; }
  .audit-heading h2 { font-size: 1.25rem; }
  .audit-dialog .audit-body { padding: 1rem; }
  .audit-section { padding: 1rem; }
  .audit-fields, .audit-feedback-grid { grid-template-columns: minmax(0, 1fr); }
  .audit-dialog .audit-footer { padding: 0.875rem 1rem; gap: 0.75rem; }
  .audit-footer-actions { width: 100%; margin: 0; }
  .audit-footer-actions .v-btn { flex: 1; }
}

@media (max-width: 37.4375rem) {
  .audit-dialog { border: 0; border-radius: 0; }
}
</style>
